import { MountayaElementsError } from "../../errors/rules/errors.js";
import type { UnitSlope, UnitTemperature } from "./preferences.js";

/**
 * Every unit conversion the SDK makes: the factors, the formulas and the unit ids, in one place.
 *
 * A factor written twice is two decisions that stay equal only until somebody improves one of
 * them. A foot of `1 / 3.28084` meters beside a mile of 1,609.344 meters makes a mile 5,280.0002
 * feet, and a port that copies the figure copies the error. So each factor is written here once,
 * from the value that defines it, and every package in this family imports it rather than
 * restating it.
 *
 * **Exact by construction.** Every table holds whole numbers: a length in tenths of a millimeter,
 * the largest unit in which every defining value is whole (the international inch is exactly
 * 2.54 cm, so 254 of them), a duration in milliseconds, an amount of data in bits and a proportion
 * in percent. A conversion reduces the ratio of two whole numbers and multiplies before it
 * divides. A whole-number ratio or its reciprocal therefore costs one rounding, bit for bit what
 * `meters / 1000` computes by hand, and a mile is 5,280 feet with no remainder.
 *
 * **A unit is validated and a value is not.** A JavaScript caller can pass any string as a unit,
 * so an unknown one throws `MountayaElementsError` naming the conversion, the parameter, the value
 * and the units it accepts. A value converts as IEEE arithmetic does: NaN stays NaN, an infinity
 * stays infinite, and a rate of zero is an infinite pace. Deciding that a value is absent is a
 * display's business, and the formatters in `./profile` state an absent or non-finite value as an
 * empty string.
 *
 * It imports nothing but the error family and the preference vocabularies beside it, so a port
 * writes it before `geo` and `profile`, which import it. `@mountaya/client` makes no measurement
 * conversion (its arithmetic is protocol time), which is why the home is here rather than in the
 * transport.
 */

/**
 * DEGREES_TO_RADIANS converts an angle in degrees into the radians trigonometry takes: multiply
 * by it. {@link RADIANS_TO_DEGREES} is its inverse.
 *
 * @example
 * ```ts
 * declare const latitude: number;
 *
 * Math.cos(latitude * DEGREES_TO_RADIANS); // how far a degree of longitude shrinks there
 * ```
 */
export const DEGREES_TO_RADIANS = Math.PI / 180;

/**
 * RADIANS_TO_DEGREES converts an angle in radians, the unit `Math.atan2` answers in, into degrees:
 * multiply by it. {@link DEGREES_TO_RADIANS} is its inverse.
 *
 * @example
 * ```ts
 * declare const y: number;
 * declare const x: number;
 *
 * Math.atan2(y, x) * RADIANS_TO_DEGREES; // the angle of the vector, in degrees
 * ```
 */
export const RADIANS_TO_DEGREES = 180 / Math.PI;

/**
 * DEGREES_PER_TURN is the degrees in one full turn, the span a bearing or a longitude wraps by.
 *
 * @example
 * ((-90 % DEGREES_PER_TURN) + DEGREES_PER_TURN) % DEGREES_PER_TURN; // 270, a bearing in [0, 360)
 */
export const DEGREES_PER_TURN = 360;

/**
 * METERS_PER_KILOMETER is the meters in one kilometer, the factor between the meters the wire
 * carries a distance in and the kilometers a reader reads it in. Geometry code composes it at a
 * fixed direction; {@link convertLength} is the conversion where the caller names both units.
 *
 * @example
 * 12_430 / METERS_PER_KILOMETER; // 12.43
 */
export const METERS_PER_KILOMETER = 1_000;

/**
 * MILLISECONDS_PER_SECOND is the milliseconds in one second, the unit a timer and an epoch take.
 *
 * @example
 * Math.floor(Date.now() / MILLISECONDS_PER_SECOND); // the seconds since the epoch
 */
export const MILLISECONDS_PER_SECOND = 1_000;

/**
 * SECONDS_PER_MINUTE is the seconds in one minute: the granularity a pace clock splits on and a
 * rate control decides a revision by.
 *
 * @example
 * Math.round(13.333 * SECONDS_PER_MINUTE); // 800, a 13:20 pace in whole seconds
 */
export const SECONDS_PER_MINUTE = 60;

/**
 * MINUTES_PER_HOUR is the minutes in one hour, where a split duration carries its minutes.
 *
 * @example
 * 135 % MINUTES_PER_HOUR; // 15, the minutes past two whole hours
 */
export const MINUTES_PER_HOUR = 60;

/**
 * SECONDS_PER_HOUR is the seconds in one hour, the factor between the seconds a duration arrives
 * in and the hours a long one reads in.
 *
 * @example
 * 5_400 / SECONDS_PER_HOUR; // 1.5
 */
export const SECONDS_PER_HOUR = 3_600;

/** The hours in one day, which the duration table in whole milliseconds needs and nothing else. */
const HOURS_PER_DAY = 24;

/**
 * LengthUnit is a unit a length is stated in, spelled as the ECMA-402 unit identifier so a value
 * converted into one formats under the same id.
 *
 * It names a unit where `UnitLength` names a measurement system: their members are disjoint, so
 * passing one where the other belongs fails at the call site. `distanceUnitFor` and
 * `elevationUnitFor` in `./profile` resolve a system into its unit.
 *
 * @example
 * // numberFormat is ./profile's memoized Intl.NumberFormat.
 * const unit: LengthUnit = "mile";
 * const miles = convertLength(12_430, "meter", unit); // 7.7236…
 * numberFormat("en", { style: "unit", unit }).format(miles); // "7.724 mi"
 */
export type LengthUnit = "centimeter" | "foot" | "inch" | "kilometer" | "meter" | "mile";

/**
 * SpeedUnit is a unit a travel rate is stated in: a rate (distance per time) or a pace (time per
 * distance, its reciprocal), each spelled as the ECMA-402 unit identifier, so a rate converted
 * into one formats under the same id. The wire carries every rate in `"kilometer-per-hour"`.
 *
 * `speedUnitFor` in `./profile` resolves the unit a reader asked for from their `UnitLength` and
 * `UnitPace`, and {@link isPaceUnit} tells the two forms apart.
 *
 * @example
 * const unit: SpeedUnit = speedUnitFor("imperial", "per-time"); // "mile-per-hour"
 * convertSpeed(8, "kilometer-per-hour", unit); // 4.9709…
 */
export type SpeedUnit =
  | "kilometer-per-hour"
  | "meter-per-second"
  | "mile-per-hour"
  | "minute-per-kilometer"
  | "minute-per-mile";

/**
 * DurationUnit is a unit a duration is stated in, spelled as the ECMA-402 unit identifier. Each
 * holds a fixed number of milliseconds, which a month and a year do not, so neither is one.
 *
 * @example
 * const unit: DurationUnit = "minute";
 * convertDuration(90_000, "millisecond", unit); // 1.5
 */
export type DurationUnit = "day" | "hour" | "millisecond" | "minute" | "second";

/**
 * SlopeUnit is a unit a gradient is stated in: the units a reader can ask for (`UnitSlope`,
 * `"degrees"` and `"percentage"`) and the bare `"ratio"` of the rise over the run, which is what
 * geometry measures. The preference's spelling is kept rather than Intl's `"percent"`, because it
 * is the value an application holds.
 *
 * @example
 * const unit: SlopeUnit = "percentage";
 * convertSlope(0.08, "ratio", unit); // 8, the grade a road sign states
 */
export type SlopeUnit = UnitSlope | "ratio";

/**
 * DataSizeUnit is a unit an amount of data is stated in. The bit, the byte and their decimal
 * multiples are spelled as the ECMA-402 unit identifier, so a size converted into one formats
 * under the same id, and their prefixes are decimal, as ECMA-402 and every bit rate mean them: a
 * kilobyte is 1,000 bytes and a megabit 1,000,000 bits, never 1,024 of either.
 *
 * `"kibibyte"` and `"mebibyte"` are the binary units: 1,024 bytes and 1,024 kibibytes. They are
 * the IEC 80000-13 names (KiB, MiB), which ECMA-402's unit list does not carry, so
 * `Intl.NumberFormat` throws a `RangeError` for either as a `unit` and a size in one is formatted
 * as a plain number. They are here because the API states its body ceilings in them (an upload of
 * at most 25 MiB), and 25 MiB read as 25 megabytes is 1,214,400 bytes short of the ceiling.
 *
 * @example
 * // numberFormat is ./profile's memoized Intl.NumberFormat.
 * const unit: DataSizeUnit = "megabyte";
 * const size = convertDataSize(12 * 20, "megabit", unit); // 30, twenty seconds at 12 Mbps
 * numberFormat("en", { style: "unit", unit }).format(size); // "30 MB"
 *
 * @example
 * ```ts
 * declare const file: Blob;
 *
 * // A ceiling of 26214400 bytes, stated as 25 MiB.
 * file.size <= convertDataSize(25, "mebibyte", "byte");
 * ```
 */
export type DataSizeUnit =
  | "bit"
  | "byte"
  | "kibibyte"
  | "kilobit"
  | "kilobyte"
  | "mebibyte"
  | "megabit"
  | "megabyte";

/**
 * ProportionUnit is a unit a proportion of a whole is stated in: the bare `"ratio"` a fraction is
 * computed as, where half way reads 0.5, and the `"percent"` a reader reads, where it reads 50.
 * `"percent"` is the ECMA-402 unit identifier, and `"ratio"` is the bare number, as it is in
 * {@link SlopeUnit}. A gradient takes a {@link SlopeUnit} instead: a grade in degrees is no
 * proportion of anything.
 *
 * @example
 * const unit: ProportionUnit = "percent";
 * Math.round(convertProportion(0.585, "ratio", unit)); // 59, the field a position reads in
 */
export type ProportionUnit = "percent" | "ratio";

/**
 * The length of each unit in tenths of a millimeter, the largest unit in which every defining
 * value is a whole number: the international inch is exactly 2.54 cm, a foot is twelve inches and
 * a mile is 5,280 feet.
 */
const lengthsInTenthsOfMillimeter: Readonly<Record<LengthUnit, number>> = {
  centimeter: 100,
  foot: 12 * 254,
  inch: 254,
  kilometer: METERS_PER_KILOMETER * 10_000,
  meter: 10_000,
  mile: 5_280 * 12 * 254,
};

/** The length of each unit in whole milliseconds. */
const durationsInMilliseconds: Readonly<Record<DurationUnit, number>> = {
  day: MILLISECONDS_PER_SECOND * SECONDS_PER_HOUR * HOURS_PER_DAY,
  hour: MILLISECONDS_PER_SECOND * SECONDS_PER_HOUR,
  millisecond: 1,
  minute: MILLISECONDS_PER_SECOND * SECONDS_PER_MINUTE,
  second: MILLISECONDS_PER_SECOND,
};

/** The bits in one byte, which the data table needs and nothing else. */
const BITS_PER_BYTE = 8;

/** The bytes in one kibibyte and the kibibytes in one mebibyte: the binary prefix, 2 to the 10. */
const BINARY_PREFIX = 1_024;

/**
 * The size of each data unit in bits: a byte is eight, a kilo or mega prefix is a power of a
 * thousand, the decimal prefixes ECMA-402 and a bit rate mean, and a kibi or mebi prefix is a
 * power of {@link BINARY_PREFIX}.
 */
const dataSizesInBits: Readonly<Record<DataSizeUnit, number>> = {
  bit: 1,
  byte: BITS_PER_BYTE,
  kibibyte: BINARY_PREFIX * BITS_PER_BYTE,
  kilobit: 1_000,
  kilobyte: 1_000 * BITS_PER_BYTE,
  mebibyte: BINARY_PREFIX * BINARY_PREFIX * BITS_PER_BYTE,
  megabit: 1_000_000,
  megabyte: 1_000_000 * BITS_PER_BYTE,
};

/** Each proportion unit's whole in percent: a ratio of one is a hundred percent. */
const proportionsInPercent: Readonly<Record<ProportionUnit, number>> = {
  percent: 1,
  ratio: 100,
};

/** A speed unit as the length and the duration it is made of, and which way round it reads. */
interface SpeedDefinition {
  /** The length a rate covers, or a pace is stated per. */
  readonly length: LengthUnit;
  /** Whether the unit states time per distance, a pace, rather than distance per time. */
  readonly pace: boolean;
  /** The duration a rate is stated per, or a pace takes. */
  readonly time: DurationUnit;
}

/** Each speed unit as a length and a duration. */
const speedDefinitions: Readonly<Record<SpeedUnit, SpeedDefinition>> = {
  "kilometer-per-hour": { length: "kilometer", pace: false, time: "hour" },
  "meter-per-second": { length: "meter", pace: false, time: "second" },
  "mile-per-hour": { length: "mile", pace: false, time: "hour" },
  "minute-per-kilometer": { length: "kilometer", pace: true, time: "minute" },
  "minute-per-mile": { length: "mile", pace: true, time: "minute" },
};

/** A temperature scale as the two conversions through Celsius. */
interface TemperatureScale {
  readonly fromCelsius: (celsius: number) => number;
  readonly toCelsius: (value: number) => number;
}

/**
 * Each temperature scale through Celsius. Fahrenheit is nine fifths of a Celsius degree offset by
 * 32, applied as a multiplication by nine and then a division by five: 1.8 is not exact in
 * binary, and `37 * 1.8 + 32` is 98.60000000000001 where `37 * 9 / 5 + 32` is 98.6.
 */
const temperatureScales: Readonly<Record<UnitTemperature, TemperatureScale>> = {
  celsius: { fromCelsius: (celsius) => celsius, toCelsius: (celsius) => celsius },
  fahrenheit: {
    fromCelsius: (celsius) => (celsius * 9) / 5 + 32,
    toCelsius: (fahrenheit) => ((fahrenheit - 32) * 5) / 9,
  },
};

/** A slope unit as the two conversions through the rise-over-run ratio. */
interface SlopeScale {
  readonly fromRatio: (ratio: number) => number;
  readonly toRatio: (value: number) => number;
}

/**
 * Each slope unit through the rise over the run: a grade is that ratio as a proportion in
 * percent, through {@link convertProportion}, and an angle is its arctangent, so forty-five
 * degrees is a hundred percent rather than forty-five.
 */
const slopeScales: Readonly<Record<SlopeUnit, SlopeScale>> = {
  degrees: {
    fromRatio: (ratio) => Math.atan(ratio) * RADIANS_TO_DEGREES,
    toRatio: (degrees) => Math.tan(degrees * DEGREES_TO_RADIANS),
  },
  percentage: {
    fromRatio: (ratio) => convertProportion(ratio, "ratio", "percent"),
    toRatio: (percentage) => convertProportion(percentage, "percent", "ratio"),
  },
  ratio: { fromRatio: (ratio) => ratio, toRatio: (ratio) => ratio },
};

/**
 * received states a unit argument the way an error message quotes it: a string in quotes, a
 * primitive as itself, and anything else by its kind, with the article the kind takes.
 */
const received = (unit: unknown): string => {
  if (typeof unit === "string") {
    return JSON.stringify(unit);
  }

  if (
    unit === undefined ||
    unit === null ||
    typeof unit === "number" ||
    typeof unit === "boolean"
  ) {
    return String(unit);
  }

  if (typeof unit === "object") {
    return Array.isArray(unit) ? "an array" : "an object";
  }

  // A function, a symbol or a bigint, each a kind that takes "a".
  return `a ${typeof unit}`;
};

/**
 * entryOf reads the entry of one unit a conversion was called with from one of the tables above,
 * or throws the error a caller can act on.
 *
 * The unit arrives in the one-member object the conversion wraps it in (`{ from }`, `{ to }`), so
 * the message names the parameter by the name its signature gives it rather than by a copy of that
 * name. `Object.hasOwn` rather than a plain read, so a unit named after an inherited property
 * (`constructor`, `toString`) is refused rather than read.
 */
const entryOf = <Unit extends string, Entry>(
  table: Readonly<Record<Unit, Entry>>,
  conversion: string,
  parameter: Readonly<Record<string, Unit>>,
): Entry => {
  // `for…in` over the one member, so the call that succeeds builds no array: a conversion runs
  // per sample and per frame. The list of accepted units is built on the refusal alone.
  let name = "";
  let unit: Unit | undefined;
  for (const key in parameter) {
    name = key;
    unit = parameter[key];
    break;
  }
  if (typeof unit === "string" && Object.hasOwn(table, unit)) {
    return table[unit];
  }

  const accepted = Object.keys(table)
    .map((key) => JSON.stringify(key))
    .join(", ");

  throw new MountayaElementsError(
    `${conversion}: "${name}" is ${received(unit)}; use one of ${accepted}.`,
  );
};

/** The greatest common divisor of two positive whole numbers, by Euclid's algorithm. */
const greatestCommonDivisor = (a: number, b: number): number => {
  let larger = a;
  let smaller = b;
  while (smaller !== 0) {
    [larger, smaller] = [smaller, larger % smaller];
  }

  return larger;
};

/**
 * scale multiplies a value by the ratio of two whole numbers, reduced first. A ratio that reduces
 * to a whole number, or to one over a whole number, is a single multiplication or division; any
 * other multiplies before it divides.
 */
const scale = (value: number, numerator: number, denominator: number): number => {
  const divisor = greatestCommonDivisor(numerator, denominator);
  const up = numerator / divisor;
  const down = denominator / divisor;

  if (down === 1) {
    return value * up;
  }

  return up === 1 ? value / down : (value * up) / down;
};

/**
 * invert divides the ratio of two whole numbers, reduced first, by a value: the reciprocal a pace
 * is of a rate. A value of zero answers an infinity, as IEEE division does.
 */
const invert = (value: number, numerator: number, denominator: number): number => {
  const divisor = greatestCommonDivisor(numerator, denominator);
  const up = numerator / divisor;
  const down = denominator / divisor;

  return down === 1 ? up / value : up / (value * down);
};

/**
 * convertLength states a length given in one unit in another, through the exact factors that
 * define each: a kilometer is 1,000 m, an international foot 0.3048 m, an inch 2.54 cm and a mile
 * 1,609.344 m, so a mile is 5,280 feet rather than 5,280.0002.
 *
 * A value converts as IEEE arithmetic does, so NaN and the infinities pass through. A unit
 * converted to itself returns the value unchanged.
 *
 * @throws MountayaElementsError when `from` or `to` is not a {@link LengthUnit}, naming which.
 * @example
 * convertLength(1, "mile", "foot"); // 5280
 * convertLength(12_430, "meter", "kilometer"); // 12.43
 */
export const convertLength = (value: number, from: LengthUnit, to: LengthUnit): number => {
  const source = entryOf(lengthsInTenthsOfMillimeter, "convertLength", { from });
  const target = entryOf(lengthsInTenthsOfMillimeter, "convertLength", { to });

  return scale(value, source, target);
};

/**
 * convertSpeed states a travel rate given in one unit in another, a rate and a pace included: a
 * pace is the reciprocal of a rate, stated in decimal minutes rather than as a clock.
 *
 * Each unit is a length and a duration from the tables {@link convertLength} and
 * {@link convertDuration} read, so 36 km/h is exactly 10 m/s and a mile per hour exactly
 * 1.609344 km/h. A value converts as IEEE arithmetic does, so across the reciprocal a rate of zero
 * is an infinite pace; `kilometersPerHourToDisplay` in `./profile` is the display-edge reading
 * that answers null there instead.
 *
 * @throws MountayaElementsError when `from` or `to` is not a {@link SpeedUnit}, naming which.
 * @example
 * convertSpeed(36, "kilometer-per-hour", "meter-per-second"); // 10
 * convertSpeed(4.5, "kilometer-per-hour", "minute-per-kilometer"); // 13.333…, which reads 13:20
 */
export const convertSpeed = (value: number, from: SpeedUnit, to: SpeedUnit): number => {
  const source = entryOf(speedDefinitions, "convertSpeed", { from });
  const target = entryOf(speedDefinitions, "convertSpeed", { to });
  const sourceLength = lengthsInTenthsOfMillimeter[source.length];
  const sourceTime = durationsInMilliseconds[source.time];
  const targetLength = lengthsInTenthsOfMillimeter[target.length];
  const targetTime = durationsInMilliseconds[target.time];

  // A rate is its length over its time and a pace its time over its length, so a conversion
  // between two of one form scales by the ratio of the parts, and one across the forms inverts.
  if (source.pace === target.pace) {
    return source.pace
      ? scale(value, sourceTime * targetLength, sourceLength * targetTime)
      : scale(value, sourceLength * targetTime, sourceTime * targetLength);
  }

  return source.pace
    ? invert(value, sourceLength * targetTime, sourceTime * targetLength)
    : invert(value, sourceTime * targetLength, sourceLength * targetTime);
};

/**
 * convertDuration states a duration given in one unit in another, through the whole milliseconds
 * each holds: a minute is 60 seconds, an hour 3,600 and a day 86,400.
 *
 * A value converts as IEEE arithmetic does, so NaN and the infinities pass through.
 *
 * @throws MountayaElementsError when `from` or `to` is not a {@link DurationUnit}, naming which.
 * @example
 * convertDuration(90, "second", "minute"); // 1.5
 */
export const convertDuration = (value: number, from: DurationUnit, to: DurationUnit): number => {
  const source = entryOf(durationsInMilliseconds, "convertDuration", { from });
  const target = entryOf(durationsInMilliseconds, "convertDuration", { to });

  return scale(value, source, target);
};

/**
 * convertTemperature states a temperature given on one scale on another: Fahrenheit is nine
 * fifths of a Celsius degree, offset by 32, so the two scales cross at minus forty.
 *
 * A value converts as IEEE arithmetic does, so NaN and the infinities pass through. A scale
 * converted to itself returns the value unchanged.
 *
 * @throws MountayaElementsError when `from` or `to` is not a `UnitTemperature`, naming which.
 * @example
 * convertTemperature(37, "celsius", "fahrenheit"); // 98.6
 */
export const convertTemperature = (
  value: number,
  from: UnitTemperature,
  to: UnitTemperature,
): number => {
  const source = entryOf(temperatureScales, "convertTemperature", { from });
  const target = entryOf(temperatureScales, "convertTemperature", { to });

  return from === to ? value : target.fromCelsius(source.toCelsius(value));
};

/**
 * convertSlope states a gradient given in one unit in another, through the rise over the run: a
 * grade is that ratio in hundredths and an angle its arctangent, so forty-five degrees is a
 * hundred percent, not forty-five. The sign is kept, so a descent stays negative.
 *
 * A run of zero is the caller's to decide before the ratio exists: each measurement that divides
 * by one keeps its own policy (no reading, a skipped sample, a floored run), because those
 * policies differ on purpose. A unit converted to itself returns the value unchanged.
 *
 * @throws MountayaElementsError when `from` or `to` is not a {@link SlopeUnit}, naming which.
 * @example
 * ```ts
 * declare const rise: number;
 * declare const run: number;
 *
 * convertSlope(100, "percentage", "degrees"); // 45
 * convertSlope(rise / run, "ratio", "degrees"); // the angle a climb reads as
 * ```
 */
export const convertSlope = (value: number, from: SlopeUnit, to: SlopeUnit): number => {
  const source = entryOf(slopeScales, "convertSlope", { from });
  const target = entryOf(slopeScales, "convertSlope", { to });

  return from === to ? value : target.fromRatio(source.toRatio(value));
};

/**
 * convertDataSize states an amount of data given in one unit in another, through the bits each
 * holds: a byte is eight bits, a decimal prefix a power of a thousand and a binary prefix a power
 * of 1,024, so a megabyte is eight megabits, a kilobyte 1,000 bytes and a mebibyte 1,048,576
 * bytes. A bit rate converts through it too, the second being the same on both sides: 12 megabits
 * per second is 12,000,000 bits per second.
 *
 * A value converts as IEEE arithmetic does, so NaN and the infinities pass through. A unit
 * converted to itself returns the value unchanged.
 *
 * @throws MountayaElementsError when `from` or `to` is not a {@link DataSizeUnit}, naming which.
 * @example
 * convertDataSize(12 * 20, "megabit", "megabyte"); // 30, twenty seconds at 12 Mbps
 * convertDataSize(12, "megabit", "bit"); // 12000000, the bit rate an encoder takes
 * convertDataSize(25, "mebibyte", "byte"); // 26214400, where 25 megabytes is 25000000
 */
export const convertDataSize = (value: number, from: DataSizeUnit, to: DataSizeUnit): number => {
  const source = entryOf(dataSizesInBits, "convertDataSize", { from });
  const target = entryOf(dataSizesInBits, "convertDataSize", { to });

  return scale(value, source, target);
};

/**
 * convertProportion states a proportion of a whole given in one unit in another: the whole is a
 * ratio of one and a hundred percent, so half way is 0.5 as a ratio and 50 in percent. It is the
 * one multiplication or division by a hundred a caller would write, so a converted value is bit
 * for bit what that arithmetic gives.
 *
 * A value converts as IEEE arithmetic does, and nothing is clamped: a proportion past the whole
 * converts as one within it does. A unit converted to itself returns the value unchanged.
 *
 * @throws MountayaElementsError when `from` or `to` is not a {@link ProportionUnit}, naming which.
 * @example
 * Math.round(convertProportion(0.585, "ratio", "percent")); // 59
 * convertProportion(50, "percent", "ratio"); // 0.5
 */
export const convertProportion = (
  value: number,
  from: ProportionUnit,
  to: ProportionUnit,
): number => {
  const source = entryOf(proportionsInPercent, "convertProportion", { from });
  const target = entryOf(proportionsInPercent, "convertProportion", { to });

  return scale(value, source, target);
};

/**
 * isPaceUnit tells whether a speed unit states time per distance, a pace such as minutes per
 * kilometer, rather than distance per time. A pace reads as a clock (`13:20`) where a rate reads
 * as a decimal, and a control steps it in seconds rather than in tenths.
 *
 * @example
 * ```ts
 * import type { UnitLength, UnitPace } from "@mountaya/client";
 *
 * declare const preferences: { readonly length: UnitLength; readonly pace: UnitPace };
 *
 * // True for a reader who asked for "per-distance".
 * isPaceUnit(speedUnitFor(preferences.length, preferences.pace));
 * ```
 */
export const isPaceUnit = (unit: SpeedUnit): unit is "minute-per-kilometer" | "minute-per-mile" =>
  Object.hasOwn(speedDefinitions, unit) && speedDefinitions[unit].pace;
